<template>
  <ConfirmDialog />
  <div class="app-shell" :class="{ 'page-transitioning': transitioning }">
    <!-- Animated Mesh Background -->
    <div class="fixed inset-0 pointer-events-none -z-10 overflow-hidden bg-gray-50 dark:bg-navy-900 transition-colors duration-500">
      <div class="mesh-blob text-blue-300/50 dark:text-blue-600/25 w-[520px] h-[520px] top-[-12%] left-[-8%]"></div>
      <div class="mesh-blob text-purple-300/40 dark:text-purple-800/30 w-[620px] h-[620px] bottom-[-22%] right-[-8%] animation-delay-2000"></div>
      <div class="mesh-blob text-teal-200/50 dark:text-teal-800/25 w-[420px] h-[420px] top-[35%] left-[45%] animation-delay-4000"></div>
    </div>

    <router-view />
    <ToastContainer />
    <AuthModal />
    <GuestLimitModal />
    <PremiumLimitModal />

    <!-- Logout Loader -->
    <transition name="fade">
      <div v-if="authStore.isSigningOut" class="fixed inset-0 z-[9999] bg-white/40 dark:bg-navy-900/40 backdrop-blur-md flex flex-col items-center justify-center">
        <div class="h-10 w-10 rounded-full border-[3px] border-gray-200 dark:border-gray-700 border-t-blue-brand animate-spin mb-4"></div>
        <p class="text-gray-600 dark:text-gray-300 font-medium text-sm">{{ t.nav.signingOut }}</p>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import ToastContainer from '@/components/ui/ToastContainer.vue'
import AuthModal from '@/components/ui/AuthModal.vue'
import GuestLimitModal from '@/components/ui/GuestLimitModal.vue'
import PremiumLimitModal from '@/components/ui/PremiumLimitModal.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import { useLanguage } from '@/composables/useLanguage'
import { useAuthStore } from '@/stores/auth'
import { useDarkMode } from '@/composables/useDarkMode'
import { usePageTransition } from '@/composables/usePageTransition'
import { useAccessibility } from '@/composables/useAccessibility'

const { t, currentLang } = useLanguage()
const { transitioning } = usePageTransition()
const authStore = useAuthStore()

useDarkMode()
useAccessibility()

onMounted(() => {
  document.documentElement.lang = currentLang.value
  authStore.checkSession()
})

import { useHead } from '@vueuse/head'
import { computed } from 'vue'

useHead({
  htmlAttrs: {
    lang: computed(() => currentLang.value),
  },
  title: computed(() => t.value.seo?.title || "cvLab — CV va Resume Builder | O'zbekiston"),
  meta: [
    { name: 'description', content: computed(() => t.value.seo?.desc || "CV yarating") },
    { name: 'keywords', content: computed(() => t.value.seo?.keywords || "cv, resume") },
    { property: 'og:title', content: computed(() => t.value.seo?.title || "cvLab — CV va Resume Builder") },
    { property: 'og:description', content: computed(() => t.value.seo?.desc || "CV yarating") },
    { property: 'og:locale', content: computed(() => currentLang.value === 'ru' ? 'ru_RU' : (currentLang.value === 'en' ? 'en_US' : 'uz_UZ')) },
  ]
})
</script>

<style>
.app-shell {
  transition: opacity 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.page-transitioning {
  opacity: 0;
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-transitioning,
.page-transitioning * {
  pointer-events: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
